import type { GpuCaps } from './capability';
import type { GpuContextLossState } from './context-loss';

export interface GpuDiagnosticsInput {
  readonly userAgent: string;
  readonly isIpadLike: boolean;
  readonly gpuEnabled: boolean;
  readonly available: boolean;
  readonly caps: GpuCaps | null;
  readonly contextLoss: Pick<
    GpuContextLossState,
    'contextLossCount' | 'lastContextLossTimestamp'
  >;
  readonly currentCapabilitySet?: readonly string[];
  readonly lastShaderCompileError?: string | null;
}

export interface GpuDiagnosticsPayload {
  readonly userAgent: string;
  readonly isIpadLike: boolean;
  readonly gpuEnabled: boolean;
  readonly available: boolean;
  readonly caps: {
    readonly maxTextureSize: number | null;
    readonly maxRenderbufferSize: number | null;
    readonly vendor: string;
    readonly renderer: string;
    readonly precisionFormat: string;
  };
  readonly contextLossCount: number;
  readonly lastContextLossTimestamp: number | null;
  readonly currentCapabilitySet: readonly string[];
  readonly lastShaderCompileError: string | null;
}

export function createGpuDiagnosticsPayload(
  input: GpuDiagnosticsInput,
): GpuDiagnosticsPayload {
  return {
    userAgent: input.userAgent,
    isIpadLike: input.isIpadLike,
    gpuEnabled: input.gpuEnabled,
    available: input.available,
    caps: input.caps
      ? {
          maxTextureSize: input.caps.maxTextureSize,
          maxRenderbufferSize: input.caps.maxRenderbufferSize,
          vendor: input.caps.vendor,
          renderer: input.caps.renderer,
          precisionFormat: input.caps.precisionFormat,
        }
      : {
          maxTextureSize: null,
          maxRenderbufferSize: null,
          vendor: 'unavailable',
          renderer: 'unavailable',
          precisionFormat: 'unavailable',
        },
    contextLossCount: input.contextLoss.contextLossCount,
    lastContextLossTimestamp: input.contextLoss.lastContextLossTimestamp,
    currentCapabilitySet: input.currentCapabilitySet ?? [],
    lastShaderCompileError: input.lastShaderCompileError ?? null,
  };
}

// ---------------------------------------------------------------------------
// Per-frame render diagnostics (B0.1+)
// Distinct from the device-level GpuDiagnosticsPayload above, which is used
// by the About dialog. These types describe what happened in a single render
// frame -- which backend ran, which labels were GPU-rendered vs CPU-fallback,
// timing, and error details.
// ---------------------------------------------------------------------------

/**
 * Identifies which backend produced a rendered frame.
 *
 * - 'cpu': no GPU code ran; CPU oracle processed all regions.
 * - 'gpu-webgl2': all regions rendered via WebGL2 backend.
 * - 'gpu-webgpu': all regions rendered via WebGPU backend (B0.6+).
 * - 'gpu-metal-native': all regions rendered via Metal/native backend (B0.7+).
 * - 'mixed': some regions rendered on GPU, others fell back to CPU.
 * - 'fallback-cpu-after-gpu-error': GPU was attempted but threw a runtime
 *   error; all regions fell back to CPU. The 'gpuError' field is populated.
 */
export type RenderBackend =
  | 'cpu'
  | 'gpu-webgl2'
  | 'gpu-webgpu'
  | 'gpu-metal-native'
  | 'mixed'
  | 'fallback-cpu-after-gpu-error';

/**
 * Per-frame diagnostics recorded by pipeline.ts and surfaced to the status
 * bar and the About dialog JSON copy.
 */
export interface GpuRenderDiagnostics {
  backend: RenderBackend;
  attemptedBackend?: RenderBackend;
  readbackCount: number;
  renderedLabels: number[];
  fallbackLabels: number[];
  /** Per-label unsupported method/stamp/blend reason. Semantically distinct from gpuError. */
  fallbackReasonByLabel?: Record<number, string>;
  /**
   * Runtime failure (shader compile/link, readPixels, context loss,
   * unexpected backend exception). NEVER populated for "method not yet
   * supported" -- use fallbackReasonByLabel for that.
   */
  gpuError?: string;
  uploadMs?: number;
  shaderMs?: number;
  readbackMs?: number;
  totalMs?: number;
  textureBytes?: number;
  contextLost?: boolean;
  parityMode?: 'production' | 'smoke';
}

/**
 * Returns true if the backend value represents a frame where GPU code
 * actually rendered any output. Status UI MUST use this helper rather
 * than JS truthiness on the backend string (every non-empty string is
 * truthy, which would always evaluate true).
 */
export function isGpuLikeBackend(backend: RenderBackend): boolean {
  return (
    backend === 'gpu-webgl2' ||
    backend === 'gpu-webgpu' ||
    backend === 'gpu-metal-native' ||
    backend === 'mixed'
  );
}
